import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  API_ROUTES,
  LIMITS,
  formatKebabPrice,
  type AdminKebabContestant,
  type AdminKebabState,
  type AdminUser,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { Field } from "../Field.js";
import { apiRequest, errorMessage } from "../../lib/apiClient.js";
import { formatTimestamp } from "../../lib/format.js";
import { useFormSubmit } from "../../hooks/useFormSubmit.js";
import { confirmAdmin } from "../../hooks/useIsAdmin.js";
import "../../styles/admin.css";

export function KebabAdminTools({
  onChanged,
}: {
  onChanged?: (() => void | Promise<void>) | undefined;
}) {
  const { busy, fields, submit } = useFormSubmit();
  const [state, setState] = useState<AdminKebabState | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const reload = useCallback(async () => {
    const [board, list] = await Promise.all([
      apiRequest<AdminKebabState>(API_ROUTES.admin.kebab),
      apiRequest<AdminUser[]>(API_ROUTES.admin.users),
    ]);
    setState(board);
    setUsers(list);
    setStartDate(board.board.startDate);
    setEndDate(board.board.endDate);
    setError(null);
  }, []);

  useEffect(() => {
    reload().catch((cause: unknown) => setError(errorMessage(cause)));
  }, [reload]);

  async function notify() {
    await onChanged?.();
  }

  async function addContestant(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      const next = await apiRequest<AdminKebabState>(API_ROUTES.admin.kebabContestants, {
        method: "POST",
        body: {
          name: newName,
          userId: newUserId ? Number.parseInt(newUserId, 10) : null,
        },
      });
      setState(next);
      await notify();
    }, "Dodano zawodnika");
    if (ok) {
      setNewName("");
      setNewUserId("");
    }
  }

  async function saveChallenge(event: FormEvent) {
    event.preventDefault();
    await submit(async () => {
      const next = await apiRequest<AdminKebabState>(API_ROUTES.admin.kebabChallenge, {
        method: "PATCH",
        body: { startDate, endDate },
      });
      setState(next);
      await notify();
    }, "Zapisano daty wyzwania");
  }

  async function saveContestant(
    contestant: AdminKebabContestant,
    patch: { name: string; userId: number | null; countOffset: number },
  ) {
    await submit(async () => {
      const next = await apiRequest<AdminKebabState>(
        API_ROUTES.admin.kebabContestant(contestant.id),
        { method: "PATCH", body: patch },
      );
      setState(next);
      await notify();
    }, "Zapisano zawodnika");
  }

  async function removeContestant(contestant: AdminKebabContestant) {
    if (!confirmAdmin(`Usunąć ${contestant.name} i wszystkie zdjęcia?`)) return;
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.kebabContestant(contestant.id), { method: "DELETE" });
      await reload();
      await notify();
    }, "Usunięto zawodnika");
  }

  async function removeEntry(id: number) {
    if (!confirmAdmin("Usunąć ten kebab i zdjęcie?")) return;
    await submit(async () => {
      await apiRequest(API_ROUTES.admin.kebabEntry(id), { method: "DELETE" });
      await reload();
      await notify();
    }, "Usunięto wpis");
  }

  if (error) return <p className="hub-error">{error}</p>;
  if (!state) return <p className="hub-hint">Wczytywanie kebaba...</p>;

  return (
    <>
      <Card title="Wyzwanie" icon="📅">
        <form className="admin-toolbar" onSubmit={(event) => void saveChallenge(event)}>
          <Field label="Od" error={fields.startDate}>
            <input
              className="z-input"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
          </Field>
          <Field label="Do" error={fields.endDate}>
            <input
              className="z-input"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              required
            />
          </Field>
          <Button type="submit" size="sm" disabled={busy}>
            Zapisz daty
          </Button>
        </form>
      </Card>

      <Card title="Zawodnicy" icon="🥙">
        <form className="admin-toolbar" onSubmit={(event) => void addContestant(event)}>
          <Field label="Imię" error={fields.name}>
            <input
              className="z-input"
              value={newName}
              maxLength={LIMITS.kebabName.max}
              onChange={(event) => setNewName(event.target.value)}
              required
            />
          </Field>
          <Field label="Konto">
            <select
              className="z-select"
              value={newUserId}
              onChange={(event) => setNewUserId(event.target.value)}
            >
              <option value="">Bez konta</option>
              {users.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.username}
                </option>
              ))}
            </select>
          </Field>
          <Button type="submit" size="sm" disabled={busy}>
            Dodaj
          </Button>
        </form>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Imię</th>
                <th>Konto</th>
                <th>Korekta liczby</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.contestants.map((contestant) => (
                <KebabContestantRow
                  key={contestant.id}
                  contestant={contestant}
                  users={users}
                  busy={busy}
                  onSave={(patch) => void saveContestant(contestant, patch)}
                  onDelete={() => void removeContestant(contestant)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Wpisy" icon="📸">
        {state.entries.length === 0 ? (
          <p className="hub-hint">Brak wpisów.</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Zdjęcie</th>
                  <th>Kto</th>
                  <th>Gdzie</th>
                  <th>Cena</th>
                  <th>Data</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {entry.imageUrl ? (
                        <img className="admin-thumb" src={entry.imageUrl} alt="" />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{entry.contestantName}</td>
                    <td>{entry.location ?? "—"}</td>
                    <td>{entry.price === null ? "—" : formatKebabPrice(entry.price)}</td>
                    <td>{formatTimestamp(entry.createdAt)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busy}
                        onClick={() => void removeEntry(entry.id)}
                      >
                        Usuń
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function KebabContestantRow({
  contestant,
  users,
  busy,
  onSave,
  onDelete,
}: {
  contestant: AdminKebabContestant;
  users: AdminUser[];
  busy: boolean;
  onSave: (patch: { name: string; userId: number | null; countOffset: number }) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(contestant.name);
  const [userId, setUserId] = useState(contestant.userId ? String(contestant.userId) : "");
  const [countOffset, setCountOffset] = useState(String(contestant.countOffset));

  useEffect(() => {
    setName(contestant.name);
    setUserId(contestant.userId ? String(contestant.userId) : "");
    setCountOffset(String(contestant.countOffset));
  }, [contestant]);

  return (
    <tr>
      <td>
        <input className="z-input" value={name} onChange={(event) => setName(event.target.value)} />
      </td>
      <td>
        <select className="z-select" value={userId} onChange={(event) => setUserId(event.target.value)}>
          <option value="">Bez konta</option>
          {users.map((account) => (
            <option key={account.id} value={account.id}>
              {account.username}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="z-input"
          type="number"
          min={LIMITS.kebabCountOffset.min}
          max={LIMITS.kebabCountOffset.max}
          value={countOffset}
          onChange={(event) => setCountOffset(event.target.value)}
        />
      </td>
      <td>
        <div className="admin-row-actions">
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              onSave({
                name,
                userId: userId ? Number.parseInt(userId, 10) : null,
                countOffset: Number.parseInt(countOffset, 10) || 0,
              })
            }
          >
            Zapisz
          </Button>
          <Button size="sm" variant="danger" disabled={busy} onClick={onDelete}>
            Usuń
          </Button>
        </div>
      </td>
    </tr>
  );
}
